import React, { forwardRef, useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import type { StylesConfig, GroupBase } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { twMerge } from 'tailwind-merge';
import uniqid from 'uniqid';

import { isPrimitive } from 'helpers/forms';

import Icons from 'assets/icons';

interface SelectOption<TValue = string | number> {
  label: string;
  value: TValue;
}

interface FieldError {
  message?: string;
  [key: string]: unknown;
}

type SelectStyleOverrides = Record<string, unknown>;

const getBasicSelectStyles = (color: string, disabledColor: string, extraBasicStyles: SelectStyleOverrides): StylesConfig<SelectOption, boolean, GroupBase<SelectOption>> => ({
  control: (styles, { isDisabled }) => ({
    ...styles,
    boxShadow: 'none',
    borderColor: color,
    outline: 'none',
    padding: '4px 0',
    backgroundColor: isDisabled ? disabledColor : 'transparent',
    borderRadius: 6,
    '&:hover': {
      borderColor: color,
    },
    ...extraBasicStyles,
  }),
  singleValue: (styles) => ({
    ...styles,
    color: 'inherit',
  }),
});

const multiSelectOptionStyles: StylesConfig<SelectOption, boolean, GroupBase<SelectOption>> = {
  multiValue: (styles) => ({
    ...styles,
    display: 'flex',
    backgroundColor: 'white',
    border: '1px solid #C0CBD8',
    padding: 0,
    margin: '0px 8px 0px 0px',
    borderRadius: '6px',
    fontSize: 18,
  }),
  multiValueRemove: (styles, { isDisabled }) => ({
    ...styles,
    color: '#002D5D',
    display: isDisabled ? 'none' : 'flex',
    ':hover': {
      cursor: 'pointer',
    },
  }),
};

interface SelectProps<TOption extends SelectOption = SelectOption> {
  label?: string;
  labelFor?: string;
  fieldError?: FieldError;
  customClass?: string;
  selectClass?: string;
  selectWrapperClass?: string;
  isSearchable?: boolean;
  isOneRowLable?: boolean;
  helperText?: string;
  options?: TOption[];
  isMulti?: boolean;
  onChange?: (value: TOption | TOption[] | null) => void;
  value?: TOption['value'] | TOption | TOption[];
  errors?: unknown;
  clearInputErr?: () => void;
  disabled?: boolean;
  disabledBgColor?: string;
  labelInTop?: boolean;
  isCreateble?: boolean;
  extraStyles?: (borderColor: string, disabledBgColor: string) => SelectStyleOverrides;
  extraBasicStyles?: SelectStyleOverrides;
  required?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  'data-testid'?: string;
  classNamePrefix?: string;
  'aria-label'?: string;
  name?: string;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  // react-select pass-through for portalled menus
  menuPortalTarget?: HTMLElement | null;
  isLoading?: boolean;
  // RHF Controller `field` interop. Spread `{...field}` carries this.
  onBlur?: () => void;
}

type SelectValue = SelectOption | SelectOption[] | undefined;

const SelectImpl = forwardRef<unknown, SelectProps<SelectOption>>(
  (
    {
      label,
      labelFor = uniqid(),
      fieldError = {} as FieldError,
      customClass = '',
      selectClass = 'w-full',
      selectWrapperClass = '',
      isSearchable = true,
      isOneRowLable = true,
      helperText = '',
      options,
      isMulti = false,
      onChange,
      value: _value,
      errors: _errors,
      clearInputErr,
      disabled,
      disabledBgColor = '#F2F2F2',
      labelInTop,
      isCreateble,
      extraStyles = () => ({}),
      extraBasicStyles = {},
      required,
      placeholder = 'Select...',
      isClearable = false,
      'data-testid': dataTestId = 'select-field',
      isLoading,
      menuPortalTarget,
    },
    ref
  ) => {
    const [borderColor, setBorderColor] = useState('#C0CBD8');
    const [isNotValid, setIsNotValid] = useState(false);

    const [inputValue, setInputValue] = useState('');
    const [value, setValue] = useState<SelectValue>(_value && !isPrimitive(_value) ? _value as SelectValue : []);

    useEffect(() => {
      if (_value && isPrimitive(_value)) {
        const selected = options?.find(
          (option) => option.value === _value || option.label === _value
        );

        setValue(selected);
      } else {
        setValue(_value as SelectValue);
      }
    }, [_value, options]);

    const handleChange = (value: SelectOption | SelectOption[] | null): void => {
      if (isNotValid) clearInputErr?.();
      setValue(value || []);
      onChange?.(value);
    };

    const handleKeyDown = (event: React.KeyboardEvent): void => {
      if (!inputValue) return;

      switch (event.keyCode) {
        case 9: // Tab
        case 13: // Enter
        case 32: // Space
          createValueFromInput(event);
          break;
        default:
      }
    };

    const createValueFromInput = (event: React.SyntheticEvent): void => {
      if (inputValue && inputValue.length > 0) {
        const currentValue = Array.isArray(value) ? value : [];
        const newValue: SelectOption[] = [
          ...currentValue,
          {
            label: inputValue,
            value: inputValue,
          },
        ];

        setInputValue('');
        setValue(newValue);
        onChange?.(newValue);
        event.preventDefault();
      }
    };

    const labelStyles = twMerge(
      'font-semibold mt-3 mb-1 text-sm',
      !labelInTop && 'mb-5',
      !disabled && helperText && !isOneRowLable && 'h-16',
      !disabled && helperText && isOneRowLable && !labelInTop && 'h-11',
      isOneRowLable && 'flex h-6',
      customClass,
    );

    useEffect(() => {
      const isError = !!Object.keys(fieldError).length;

      setBorderColor(isError ? '#D91655' : '#C0CBD8');
      setIsNotValid(isError);
    }, [fieldError]);

    const CurrentSelect = isCreateble ? CreatableSelect : ReactSelect;

    const formatCreateLabel = (inputValue: string): string => `Use "${inputValue}"`;
    const errorMessage = fieldError?.message;

    const selectRef = ref as never;

    return (
      <div className={twMerge('flex flex-col w-full', selectWrapperClass)}>
        <div
          className={twMerge(
            'flex',
            labelInTop ? 'flex-col items-start' : 'items-center',
          )}
        >
          {label && (
            <label id={`${labelFor}-label`} htmlFor={labelFor} className={labelStyles}>
              {label}
              {required && !disabled ? (
                <Icons.RequiredIndicator customClass="w-[7px] h-[7px] ml-1 mt-1" aria-hidden="true" />
              ) : null}
            </label>
          )}
          <div
            className={`flex flex-col ${selectClass}`}
            data-testid={dataTestId}
          >
            {isMulti && !options ? (
              <CreatableSelect<SelectOption, true>
                id={labelFor}
                styles={{
                  ...getBasicSelectStyles(
                    borderColor,
                    disabledBgColor,
                    extraBasicStyles
                  ),
                  ...multiSelectOptionStyles,
                } as StylesConfig<SelectOption, true>}
                ref={selectRef}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
                placeholder="Type..."
                inputValue={inputValue}
                isClearable={isClearable}
                isMulti
                menuIsOpen={false}
                onChange={(newValue) => handleChange(newValue as SelectOption[])}
                onBlur={(event) => createValueFromInput(event)}
                onInputChange={(newInputValue) => {
                  if (isNotValid) clearInputErr?.();
                  setInputValue(newInputValue);
                }}
                onKeyDown={handleKeyDown}
                value={value as SelectOption[]}
                {...(label && labelFor ? { 'aria-labelledby': `${labelFor}-label` } : {})}
                isDisabled={disabled}
              />
            ) : isMulti && options ? (
              <CurrentSelect<SelectOption, true>
                isMulti
                id={labelFor}
                styles={{
                  ...getBasicSelectStyles(
                    borderColor,
                    disabledBgColor,
                    extraBasicStyles
                  ),
                  ...multiSelectOptionStyles,
                } as StylesConfig<SelectOption, true>}
                ref={selectRef}
                isSearchable={isSearchable}
                options={options}
                value={value as SelectOption[]}
                onChange={(newValue) => handleChange(newValue as SelectOption[])}
                components={{
                  IndicatorSeparator: () => null,
                }}
                {...(label && labelFor ? { 'aria-labelledby': `${labelFor}-label` } : {})}
                placeholder={disabled ? 'Not selected' : placeholder}
                isDisabled={disabled}
                formatCreateLabel={formatCreateLabel}
                isClearable={isClearable}
              />
            ) : (
              <CurrentSelect<SelectOption, false>
                id={labelFor}
                styles={{
                  ...getBasicSelectStyles(
                    borderColor,
                    disabledBgColor,
                    extraBasicStyles
                  ),
                  ...extraStyles(borderColor, disabledBgColor),
                } as StylesConfig<SelectOption, false>}
                ref={selectRef}
                isSearchable={isSearchable}
                options={options}
                value={value as SelectOption}
                onChange={(newValue) => handleChange(newValue as SelectOption | null)}
                components={{
                  IndicatorSeparator: () => null,
                }}
                {...(label && labelFor ? { 'aria-labelledby': `${labelFor}-label` } : {})}
                menuPortalTarget={menuPortalTarget}
                placeholder={disabled ? 'Not selected' : placeholder}
                isDisabled={disabled}
                isLoading={isLoading}
                formatCreateLabel={formatCreateLabel}
                isClearable={isClearable}
              />
            )}
            {errorMessage && !disabled && (
              <span
                id={`${labelFor}-error`}
                role="status"
                className="font-sm pt-1 text-critical-600 bg-white"
              >
                {errorMessage}
              </span>
            )}
            {helperText && !disabled && !errorMessage && (
              <span id={`${labelFor}-helper`} className="font-sm pt-1 bg-white">
                {helperText}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
);

SelectImpl.displayName = 'Select';

// Generic re-export so callers can write `<Select<MyOption> ... />` without `as never`.
// The forwardRef type erases generics; this cast restores them at the type layer
// without changing the runtime component.
export const Select = SelectImpl as unknown as (<TOption extends SelectOption = SelectOption>(
  props: SelectProps<TOption> & { ref?: React.Ref<unknown> }
) => React.ReactElement) & {
  displayName?: string;
};

export type { SelectOption, SelectProps };

export default Select;
